import "./style.css";
import { analytics } from "./analytics";
import { Connection } from "./network";
import { Engine } from "./engine";
import { HeldMovement } from "./held-movement";
import { guardCanvasMouse } from "./canvas-mouse";
import { InputJournal } from "./input-journal";
import { Dialogs } from "./dialogs";
import {
  INITIAL_STATE,
  type AppState,
  type MatchEvent,
  type PlayerInput,
  type Room,
  type Snapshot,
  type Spell,
} from "./types";
import {
  DEFAULT_SETTINGS,
  loadSettings,
  saveSettings,
  sanitizeSettings,
  type Settings,
} from "./settings";
import { toast } from "./dom";
import { createRenderer } from "./renderer";
import { localPlayer } from "./views/shared";
import { followTarget } from "./follow-target";
import { purchaseStatus, SPELL_COPY } from "./spells";
import { spellRank, upgradeStatus, upgradeApplied } from "./upgrades";
import { attachSpellReordering, canReorderSpells } from "./spell-reorder";
import {
  EntryGeneration,
  parseInvite,
  type InviteDestination,
} from "./session-flow";

const state: AppState = { ...INITIAL_STATE };
void analytics.start();
analytics.track("Game Opened", {
  entry: location.pathname.startsWith("/room/") ? "invite" : "home",
});
let settings = loadSettings();
const engine = new Engine(),
  connection = new Connection(),
  dialogs = new Dialogs();
const render = createRenderer(state, dialogs, () => {
  spellReordering.cancel();
  syncInput();
});
const entry = new EntryGeneration();
const inputJournal = new InputJournal();
const heldMovement = new HeldMovement((keys) => engine.movementKeys(keys));
let entryActive = false,
  ignoreRoomMessages = false;
let initialization: Promise<void> | undefined,
  engineInitialization: Promise<void> | undefined;
let inviteDestination: InviteDestination | undefined,
  lookupController: AbortController | undefined;
let pendingInvite:
  (InviteDestination & { observe: boolean; sent: boolean }) | undefined;
let entryTimer: ReturnType<typeof setTimeout> | undefined;
let purchaseTimer: ReturnType<typeof setTimeout> | undefined;
let reorderTimer: ReturnType<typeof setTimeout> | undefined;
let pendingReorder:
  | {
      matchId: string;
      playerId: number;
      round: number;
      expectedRevision: number;
      loadout: (Spell | null)[];
    }
  | undefined;
let pendingPurchase:
  | { type: "buy"; requestId: number; spell: Spell; slot: number }
  | { type: "upgrade"; requestId: number; spell: Spell; expectedRank: number }
  | undefined;
const eventHistory = new Map<number, MatchEvent>();
let lastEventId = 0,
  matchRoomId: string | undefined;
let acknowledgedPurchase: number | null = null;
let firstSnapshot = true;
let snapshotReceivedAt: number | undefined;
let currentMatchId: string | undefined;
let confirmedMatchId: string | undefined;
let purchaseMatchId: string | undefined;
let inputEnabled = false;
// A violated native admission contract requires a refresh. UI focus/menu work
// cannot re-enable prediction after the connection was deliberately closed.
let inputTimelineRejected = false;
const spellReordering = attachSpellReordering(document, {
  getState: () => state,
  onReorder: reorderSpells,
});

function configureEngine(): void {
  engine.configure(settings);
  document.documentElement.classList.toggle(
    "reduced-motion",
    settings.reducedMotion,
  );
  syncInput();
}
function syncInput(): void {
  const me = localPlayer(state);
  const ownsMovement =
    state.screen === "match" &&
    state.engineReady &&
    state.matchPlayerId != null &&
    !dialogs.kind &&
    state.connected &&
    !inputTimelineRejected &&
    !document.hidden &&
    document.hasFocus();
  // Keep physical movement intent through the countdown/round boundary. Only
  // combat can produce movement; menus, disconnects and focus loss clear intent.
  heldMovement.setActive(ownsMovement);
  const enabled = ownsMovement && state.snapshot?.phase === "combat" && !!me?.alive;
  engine.input(enabled);
  // Winit receives browser keyboard events on its canvas. Starting a round or
  // closing a menu must restore that focus before the first movement key.
  if (enabled && !inputEnabled)
    document
      .querySelector<HTMLCanvasElement>("#game-canvas")
      ?.focus({ preventScroll: true });
  inputEnabled = enabled;
}
function setScreen(screen: AppState["screen"]): void {
  state.screen = screen;
  if (screen === "loader") {
    document.querySelector("#performance")?.remove();
    document.querySelector("[data-connection-status]")?.remove();
  }
  engine.setMode(
    screen === "match"
      ? state.matchPlayerId == null
        ? "observe"
        : "match"
      : "demo",
  );
  render();
}
async function ensureEngine(): Promise<void> {
  if (state.engineReady) return;
  if (engineInitialization) return engineInitialization;
  state.engineError = null;
  const startedAt = performance.now();
  engineInitialization = engine
    .start()
    .then(() => {
      state.engineReady = true;
      analytics.track("Arena Ready", {
        load_ms: Math.round(performance.now() - startedAt),
      });
      configureEngine();
      render();
    })
    .catch((error) => {
      state.engineError = "The arena could not load.";
      analytics.track("Arena Load Failed");
      console.error("Engine startup failed", error);
      render();
      throw new Error(
        "The arena could not load. Reload the game to try again.",
      );
    })
    .finally(() => {
      engineInitialization = undefined;
    });
  return engineInitialization;
}
async function ensureConnection(): Promise<void> {
  if (inputTimelineRejected) throw new Error("Refresh to reconnect safely after the input timeline changed.");
  if (connection.blockedReason) throw new Error(connection.blockedReason);
  if (state.connected) return;
  if (initialization) return initialization;
  initialization = (async () => {
    const session = await connection.initialize();
    state.name = session.name;
    state.playerId = session.playerId;
    render();
    await new Promise<void>((resolve, reject) => {
      const started = Date.now();
      const timer = setInterval(() => {
        if (state.connected) {
          clearInterval(timer);
          resolve();
        } else if (connection.blockedReason) {
          clearInterval(timer);
          reject(new Error(connection.blockedReason));
        } else if (Date.now() - started > 12_000) {
          clearInterval(timer);
          reject(
            new Error("Could not connect to the arena. Please try again."),
          );
        }
      }, 80);
    });
  })().finally(() => {
    initialization = undefined;
  });
  return initialization;
}
function command(value: unknown): boolean {
  if (connection.send(value)) return true;
  toast("Connection interrupted. Reconnecting to your arena…");
  return false;
}
function resetMatch(): void {
  heldMovement.setActive(false);
  clearReorder();
  spellReordering.cancel();
  currentMatchId = undefined;
  matchRoomId = undefined;
  state.snapshot = null;
  snapshotReceivedAt = undefined;
  state.matchPlayerId = null;
  state.followedPlayerId = null;
  state.pendingRequest = null;
  state.pendingSpell = null;
  pendingPurchase = undefined;
  purchaseMatchId = undefined;
  confirmedMatchId = undefined;
  state.selectedSlot = 1;
  eventHistory.clear();
  inputJournal.clear();
  lastEventId = 0;
  firstSnapshot = true;
  clearTimeout(purchaseTimer);
  engine.player(null);
  engine.followPlayer(null);
}
function leave(): void {
  if (currentMatchId && state.snapshot && state.snapshot.phase !== "match_over")
    analytics.track("Match Left", {
      match_id: currentMatchId,
      role: state.matchPlayerId == null ? "observer" : "player",
      phase: state.snapshot.phase,
      round: state.snapshot.round,
    });
  entry.cancel();
  entryActive = false;
  pendingInvite = undefined;
  ignoreRoomMessages = true;
  clearTimeout(entryTimer);
  lookupController?.abort();
  connection.send({ type: "leave" });
  dialogs.close();
  state.room = null;
  matchRoomId = undefined;
  currentMatchId = undefined;
  resetMatch();
  setScreen("home");
  if (location.pathname.startsWith("/room/")) history.replaceState({}, "", "/");
}
async function enter(
  action: "play" | "create" | "join",
  observe = false,
): Promise<void> {
  if (connection.blockedReason) {
    toast(connection.blockedReason);
    return;
  }
  const generation = entry.begin();
  if (action !== "play")
    analytics.track("Room Entry Requested", {
      action,
      role: observe ? "observer" : "player",
    });
  entryActive = true;
  pendingInvite =
    action === "join" && inviteDestination
      ? { ...inviteDestination, observe, sent: false }
      : undefined;
  ignoreRoomMessages = false;
  dialogs.close();
  clearTimeout(entryTimer);
  resetMatch();
  state.room = null;
  setScreen("loader");
  entryTimer = setTimeout(() => {
    if (entry.isCurrent(generation)) {
      leave();
      toast("The arena took too long to respond. Please try again.");
    }
  }, 45_000);
  try {
    await Promise.all([ensureConnection(), ensureEngine()]);
    if (!entry.isCurrent(generation)) return;
    // Reconnecting to this same invite may already have resumed admission.
    if (action === "join" && !pendingInvite) return;
    const payload =
      action === "play"
        ? { type: "matchmake" }
        : action === "create"
          ? { type: "create_room" }
          : inviteCommand();
    if (!connection.send(payload))
      throw new Error("The connection was interrupted. Please try again.");
    if (pendingInvite) pendingInvite.sent = true;
  } catch (error) {
    if (!entry.isCurrent(generation)) return;
    leave();
    toast(
      error instanceof Error
        ? error.message
        : "Unable to enter the arena. Please try again.",
    );
  }
}
function inviteCommand(): unknown {
  if (!pendingInvite) throw new Error("Find a room invite before joining.");
  return {
    type: "join_room",
    roomId: pendingInvite.roomId,
    observe: pendingInvite.observe,
    inviteToken: pendingInvite.inviteToken,
  };
}
async function lookupInvite(value: string): Promise<void> {
  lookupController?.abort();
  const controller = (lookupController = new AbortController());
  try {
    const destination = parseInvite(value);
    const query = destination.inviteToken
      ? `?key=${encodeURIComponent(destination.inviteToken)}`
      : "";
    const response = await fetch(
      `/api/rooms/${encodeURIComponent(destination.roomId)}${query}`,
      {
        signal: AbortSignal.any([
          controller.signal,
          AbortSignal.timeout(10_000),
        ]),
      },
    );
    if (!response.ok)
      throw new Error(
        response.status === 403
          ? "This invite is no longer valid. Ask your friend for a new one."
          : response.status === 404
            ? "That room has closed. Ask your friend for a new invite."
            : "Unable to look up this room. Please try again.",
      );
    const room = (await response.json()) as Room;
    if (controller.signal.aborted) return;
    inviteDestination = destination;
    dialogs.entry(room);
  } catch (error) {
    if (controller.signal.aborted) return;
    const message =
      error instanceof Error ? error.message : "Unable to find this room.";
    if (dialogs.kind) dialogs.error(message);
    else {
      dialogs.join();
      dialogs.error(message);
    }
  }
}
function invite(): void {
  if (!state.room) return;
  dialogs.invite(
    new URL(state.room.invitePath, location.origin).href,
    state.room.leaderId === state.playerId && state.room.kind === "friends",
  );
}
function synchronizeFollow(id = state.followedPlayerId): void {
  const next = followTarget(
    state.snapshot?.players ?? [],
    state.matchPlayerId,
    id,
  );
  if (next === state.followedPlayerId) return;
  state.followedPlayerId = next;
  engine.followPlayer(next);
}
function selectFollow(id?: number): void {
  synchronizeFollow(id ?? null);
  render();
}
function cycleFollow(step: number): void {
  const players = state.snapshot?.players.filter((p) => p.alive) ?? [];
  if (!players.length) return;
  const at = players.findIndex((p) => p.id === state.followedPlayerId);
  selectFollow(players[(at + step + players.length) % players.length].id);
}
function ingestEvents(events: MatchEvent[], s?: Snapshot): void {
  for (const event of events) {
    if (eventHistory.has(event.id)) continue;
    eventHistory.set(event.id, event);
    if (
      !firstSnapshot &&
      event.id > lastEventId &&
      event.kind.type === "gold_awarded" &&
      event.kind.player === state.matchPlayerId &&
      event.kind.victim != null &&
      (!s || s.tick - event.tick < s.tick_rate * 2)
    )
      toast(
        `+${event.kind.amount} gold · ${state.snapshot?.players.find((p) => p.id === event.kind.victim)?.name ?? "Elimination"}`,
      );
    lastEventId = Math.max(lastEventId, event.id);
  }
  if (eventHistory.size > 2048) {
    const remove = eventHistory.size - 2048;
    [...eventHistory.keys()]
      .slice(0, remove)
      .forEach((id) => eventHistory.delete(id));
  }
}
function updateEngineSnapshot(): void {
  if (!state.snapshot) return;
  const events = [...eventHistory.values()].sort((a, b) => a.id - b.id);
  state.snapshot = { ...state.snapshot, events, input_timelines: inputJournal.values() };
  // Reliable events can arrive after a newer replaceable snapshot. Keep the
  // renderer's bounded event window complete without resending the full UI ledger.
  engine.snapshot(
    JSON.stringify({ ...state.snapshot, events: events.slice(-256) }),
    snapshotReceivedAt,
  );
}
function ingestInputTimelines(incoming: Snapshot["input_timelines"], sourceTick: number): boolean {
  try {
    inputJournal.merge(incoming, sourceTick);
    return true;
  } catch (error) {
    rejectAuthoritativeTiming(error);
    return false;
  }
}
function rejectAuthoritativeTiming(error: unknown): void {
  console.error("Invalid authoritative input timeline or match timing", error);
  inputTimelineRejected = true;
  state.connected = false;
  state.reconnecting = false;
  state.online = null;
  confirmedMatchId = undefined;
  inputEnabled = false;
  connection.close();
  engine.input(false);
  render();
  toast("The server changed its input timeline or timing policy. Refresh to reconnect safely.");
}
connection.onStatus = (status) => {
  document.documentElement.dataset.transport = connection.transportKind ?? "";
  state.connected = status === "connected" && !inputTimelineRejected;
  state.reconnecting = status === "reconnecting" && !inputTimelineRejected;
  if (!state.connected) state.online = null;
  // Authentication alone does not identify which match this connection resumes.
  // A pending shop intent may only retry after a matching Match message.
  if (status !== "connected") confirmedMatchId = undefined;
  render();
  syncInput();
  document.querySelector("[data-connection-status]")?.remove();
  if (
    status === "blocked" ||
    (state.reconnecting && state.screen !== "home" && state.screen !== "loader")
  ) {
    const node = document.createElement("div");
    node.dataset.connectionStatus = status;
    node.className = "connection-status";
    node.setAttribute("role", status === "blocked" ? "alert" : "status");
    node.textContent =
      status === "blocked"
        ? (connection.blockedReason ??
          "This session is already open in another tab.")
        : "Reconnecting · your seat is saved";
    document.body.append(node);
  }
};
connection.onTiming = (rttMs, sampleMs) => {
  state.latency = rttMs == null ? 0 : Math.round(rttMs);
  engine.networkTiming(sampleMs);
};
connection.onMessage = (message, _raw, receivedAt = performance.now()) => {
  if (inputTimelineRejected) return;
  switch (message.type) {
    case "welcome":
      state.name = message.name;
      state.playerId = message.playerId;
      state.online = message.online;
      state.catalog = message.catalog;
      // A reconnect may happen after sending the invite but before admission.
      // Retry that exact destination; the server's join is idempotent.
      if (entryActive && pendingInvite?.sent) connection.send(inviteCommand());
      break;
    case "presence":
      state.online = message.online;
      break;
    case "queued":
      if (pendingInvite) return;
      if (ignoreRoomMessages) {
        connection.send({ type: "leave" });
        return;
      }
      analytics.queued(message.roomId);
      break;
    case "room":
      if (ignoreRoomMessages) return;
      // Authentication resumes the previous membership before a pending join.
      // Only the requested room can finish this entry or replace its loader.
      if (pendingInvite && message.room.id !== pendingInvite.roomId) return;
      if (state.room?.id !== message.room.id)
        analytics.track("Room Joined", {
          room_kind: message.room.kind,
          mode: message.room.mode,
        });
      state.room = message.room;
      if (state.room?.kind === "friends" && !state.room.started) {
        entryActive = false;
        pendingInvite = undefined;
        clearTimeout(entryTimer);
        setScreen("room");
      }
      if (dialogs.kind === "invite") invite();
      break;
    case "match":
      if (ignoreRoomMessages) {
        connection.send({ type: "leave" });
        return;
      }
      if (pendingInvite && message.roomId !== pendingInvite.roomId) return;
      clearTimeout(entryTimer);
      entryActive = false;
      pendingInvite = undefined;
      if (
        currentMatchId !== message.matchId ||
        matchRoomId !== message.roomId
      ) {
        resetMatch();
        matchRoomId = message.roomId;
        currentMatchId = message.matchId;
      }
      state.matchPlayerId = message.playerId;
      confirmedMatchId = message.matchId;
      retryPurchase();
      retryReorder();
      engine.player(message.playerId);
      engine.simConfig(message.config);
      state.screen = "match";
      engine.setMode(message.playerId == null ? "observe" : "match");
      try {
        engine.predictionPolicy(message.matchId, message.predictionPolicy);
      } catch (error) {
        rejectAuthoritativeTiming(error);
        return;
      }
      ensureEngine().catch(() => {
        leave();
        toast("The arena could not load. Reload the game to retry.");
      });
      break;
    case "snapshot": {
      if (
        ignoreRoomMessages ||
        state.screen !== "match" ||
        !currentMatchId ||
        message.matchId !== currentMatchId ||
        confirmedMatchId !== currentMatchId
      )
        return;
      const s = message.snapshot as Snapshot;
      if (
        state.snapshot &&
        (s.tick < state.snapshot.tick || s.round < state.snapshot.round)
      )
        return;
      // Same-tick metadata can arrive on a different transport lane. Never
      // restore an older slot order after an authoritative reorder was seen.
      if (
        state.snapshot &&
        s.players.some((player) => {
          const previous = state.snapshot!.players.find(
            (old) => old.id === player.id,
          );
          return (player.loadout_revision ?? 0) < (previous?.loadout_revision ?? 0);
        })
      )
        return;
      if (!ingestInputTimelines(s.input_timelines, s.tick)) return;
      inputJournal.retireBots(s.players);
      ingestEvents(s.events, s);
      if (!state.snapshot || s.tick !== state.snapshot.tick || s.round !== state.snapshot.round)
        snapshotReceivedAt = receivedAt;
      // Reliable metadata and duplicate snapshots do not make this world newer.
      state.snapshot = s;
      analytics.snapshot(
        currentMatchId, state.matchPlayerId, s, state.room?.kind ?? "unknown",
      );
      synchronizeFollow();
      updateEngineSnapshot();
      const me = localPlayer(state);
      if (pendingReorder) {
        const revision = me?.loadout_revision ?? 0;
        if (!me || s.phase !== "shop" || s.round !== pendingReorder.round) {
          clearReorder();
        } else if (revision > pendingReorder.expectedRevision) {
          const applied = me.loadout.every(
            (spell, slot) => spell === pendingReorder!.loadout[slot],
          );
          clearReorder();
          if (!applied) toast("Your spell bar changed. Try dragging again.");
        }
      }
      if (pendingPurchase) {
        const receipts =
          pendingPurchase.type === "upgrade"
            ? s.upgrade_receipts
            : s.purchase_receipts;
        const receipt = receipts?.find(
          (item) =>
            item.request_id === pendingPurchase?.requestId &&
            item.player === state.matchPlayerId,
        );
        if (receipt)
          acknowledgePurchase(
            pendingPurchase.type === "upgrade" ? "upgraded" : "purchased",
            receipt,
          );
      }
      if (
        pendingPurchase &&
        (pendingPurchase.type === "buy"
          ? me?.loadout.includes(pendingPurchase.spell)
          : upgradeApplied(
              me,
              pendingPurchase.spell,
              pendingPurchase.expectedRank,
            ))
      )
        clearPurchase();
      if (s.phase !== "shop" && state.pendingRequest != null) clearPurchase();
      if (me?.loadout[state.selectedSlot])
        state.selectedSlot = Math.max(
          0,
          me.loadout.findIndex((spell) => !spell),
        );
      firstSnapshot = false;
      break;
    }
    case "input_timelines":
      if (!ignoreRoomMessages && state.screen === "match" &&
          currentMatchId && message.matchId === currentMatchId &&
          confirmedMatchId === currentMatchId) {
        if (!ingestInputTimelines(message.inputTimelines, state.snapshot?.tick ?? 0)) return;
        updateEngineSnapshot();
      }
      break;
    case "events":
      if (
        !ignoreRoomMessages &&
        state.screen === "match" &&
        !!currentMatchId &&
        message.matchId === currentMatchId &&
        confirmedMatchId === currentMatchId
      ) {
        ingestEvents(message.events);
        updateEngineSnapshot();
      }
      break;
    case "purchased":
    case "upgraded":
      acknowledgePurchase(message.type, message.receipt);
      break;
    case "renamed":
      if (message.playerId === state.playerId) {
        state.name = message.name;
        if (dialogs.kind === "rename") dialogs.close();
      }
      break;
    case "left":
      if (!entryActive && state.screen !== "home") {
        state.room = null;
        resetMatch();
        setScreen("home");
      }
      break;
    case "error":
      analytics.track("Game Error", { code: message.code });
      clearPurchase();
      clearReorder();
      if (message.code === "session_in_use") {
        // Only reset this rejected tab. Connection already closed before this
        // callback, so leave() cannot mutate the still-playing owner's match.
        leave();
        break;
      }
      if (entryActive) {
        if (
          pendingInvite &&
          !pendingInvite.observe &&
          ["room_full", "match_started"].includes(message.code)
        ) {
          // The room can start or fill after the invite preview. Admit this
          // same invitation as a spectator; the server still checks its key.
          pendingInvite.observe = true;
          if (connection.send(inviteCommand())) return;
        }
        const watchDestination =
          pendingInvite && ["room_full", "match_started"].includes(message.code)
            ? { ...pendingInvite }
            : null;
        leave();
        toast(message.message);
        if (watchDestination)
          void lookupInvite(
            `/room/${watchDestination.roomId}${watchDestination.inviteToken ? `?key=${encodeURIComponent(watchDestination.inviteToken)}` : ""}`,
          );
      } else if (dialogs.kind === "rename" || dialogs.kind === "entry")
        dialogs.error(message.message);
      else toast(message.message);
      break;
    case "shutdown":
      toast("The arena is restarting. Your connection will retry.");
      break;
  }
  render();
  syncInput();
};
function clearPurchase(): void {
  acknowledgedPurchase = null;
  state.pendingRequest = null;
  state.pendingSpell = null;
  pendingPurchase = undefined;
  purchaseMatchId = undefined;
  clearTimeout(purchaseTimer);
}
function clearReorder(): void {
  pendingReorder = undefined;
  state.pendingReorder = false;
  clearTimeout(reorderTimer);
}
function retryReorder(): boolean {
  const player = localPlayer(state);
  if (
    !pendingReorder ||
    !state.connected ||
    !player ||
    player.ready ||
    pendingReorder.matchId !== currentMatchId ||
    currentMatchId !== confirmedMatchId ||
    pendingReorder.playerId !== state.matchPlayerId ||
    state.snapshot?.phase !== "shop" ||
    state.snapshot.round !== pendingReorder.round
  )
    return false;
  return connection.send({
    type: "reorder_spells",
    matchId: pendingReorder.matchId,
    expectedRevision: pendingReorder.expectedRevision,
    loadout: pendingReorder.loadout,
  });
}
function reorderSpells(from: number, to: number): void {
  const player = localPlayer(state);
  if (
    !canReorderSpells(state) ||
    dialogs.kind ||
    !currentMatchId ||
    currentMatchId !== confirmedMatchId ||
    !player ||
    !Number.isInteger(from) ||
    !Number.isInteger(to) ||
    from < 0 ||
    from > 3 ||
    to < 0 ||
    to > 3 ||
    from === to ||
    !player.loadout[from]
  )
    return;
  const loadout = [...player.loadout];
  [loadout[from], loadout[to]] = [loadout[to], loadout[from]];
  pendingReorder = {
    matchId: currentMatchId,
    playerId: player.id,
    round: state.snapshot!.round,
    expectedRevision: player.loadout_revision ?? 0,
    loadout,
  };
  state.pendingReorder = true;
  if (!retryReorder()) {
    clearReorder();
    toast("Reconnect before rearranging spells.");
  } else {
    reorderTimer = setTimeout(() => {
      if (retryReorder()) toast("Confirming your spell order…");
    }, 5000);
  }
  render();
}
function acknowledgePurchase(
  type: "upgraded" | "purchased",
  receipt: { request_id: number; spell: Spell; rank_after?: number },
): void {
  if (
    receipt.request_id !== state.pendingRequest ||
    receipt.request_id === acknowledgedPurchase
  )
    return;
  acknowledgedPurchase = receipt.request_id;
  analytics.track(type === "upgraded" ? "Spell Upgraded" : "Spell Learned", {
    spell: receipt.spell,
    rank: receipt.rank_after ?? 0,
    round: state.snapshot?.round ?? 0,
  });
  toast(
    `${SPELL_COPY[receipt.spell]?.name ?? "Spell"} ${type === "upgraded" ? `upgraded to rank ${receipt.rank_after === 1 ? "I" : "II"}` : "learned"}`,
  );
}
function retryPurchase(): boolean {
  return (
    !!pendingPurchase &&
    state.connected &&
    !!purchaseMatchId &&
    purchaseMatchId === currentMatchId &&
    purchaseMatchId === confirmedMatchId &&
    connection.send(pendingPurchase)
  );
}
function buy(spell: Spell): void {
  const player = localPlayer(state),
    offer = state.catalog.find((o) => o.spell === spell);
  if (
    !state.connected ||
    !currentMatchId ||
    confirmedMatchId !== currentMatchId ||
    !offer ||
    !player ||
    state.snapshot?.phase !== "shop" ||
    purchaseStatus(
      offer,
      player,
      state.pendingRequest != null || state.pendingReorder,
    ).disabled
  )
    return;
  const slot =
    player.loadout[state.selectedSlot] == null
      ? state.selectedSlot
      : player.loadout.findIndex((spell) => !spell);
  if (slot < 0) return;
  const requestId = Date.now() * 1000 + Math.floor(Math.random() * 1000);
  pendingPurchase = { type: "buy", requestId, spell, slot };
  purchaseMatchId = currentMatchId;
  state.pendingRequest = requestId;
  state.pendingSpell = spell;
  if (!retryPurchase()) {
    clearPurchase();
    toast("Reconnect before learning a spell.");
  } else
    purchaseTimer = setTimeout(() => {
      if (retryPurchase()) {
        toast("Confirming your purchase…");
      }
    }, 5000);
  render();
}
function roomAction(action: unknown): void {
  command({ type: "room_action", action });
}
function upgrade(spell: Spell): void {
  const player = localPlayer(state);
  const offer = state.catalog.find((item) => item.spell === spell);
  if (
    !state.connected ||
    !currentMatchId ||
    confirmedMatchId !== currentMatchId ||
    !player ||
    !offer ||
    state.snapshot?.phase !== "shop" ||
    upgradeStatus(
      offer,
      player,
      state.snapshot.round,
      state.pendingRequest != null || state.pendingReorder,
    ).disabled
  )
    return;
  const requestId = Date.now() * 1000 + Math.floor(Math.random() * 1000);
  pendingPurchase = {
    type: "upgrade",
    requestId,
    spell,
    expectedRank: spellRank(player, spell),
  };
  purchaseMatchId = currentMatchId;
  state.pendingRequest = requestId;
  state.pendingSpell = spell;
  if (!retryPurchase()) {
    clearPurchase();
    toast("Reconnect before upgrading a spell.");
  } else {
    purchaseTimer = setTimeout(() => {
      if (retryPurchase()) {
        toast("Confirming your upgrade…");
      }
    }, 5000);
  }
  render();
}
async function handleAction(target: HTMLElement): Promise<void> {
  const action = target.dataset.action,
    slot = Number(target.dataset.slot);
  switch (action) {
    case "play":
    case "play-again":
      analytics.track("Play Clicked", { rematch: action === "play-again" });
      if (state.room) connection.send({ type: "leave" });
      await enter("play");
      break;
    case "create":
      await enter("create");
      break;
    case "join":
      dialogs.join();
      break;
    case "enter-play":
      await enter("join");
      break;
    case "enter-watch":
      await enter("join", true);
      break;
    case "leave":
      leave();
      break;
    case "menu":
      dialogs.menu(state);
      break;
    case "settings":
      dialogs.settings(settings);
      break;
    case "controls":
      dialogs.controls();
      break;
    case "rename":
      dialogs.rename(state);
      break;
    case "invite":
      invite();
      break;
    case "revoke-invite":
      roomAction({ type: "revoke_invite" });
      break;
    case "copy-invite": {
      const input = document.querySelector<HTMLInputElement>("#invite-url")!;
      try {
        await navigator.clipboard.writeText(input.value);
        analytics.track("Invite Copied");
        document.querySelector("[data-copy-status]")!.textContent =
          "Invite copied. Bring a friend.";
      } catch {
        input.select();
        document.querySelector("[data-copy-status]")!.textContent =
          "Select and copy this invite to share it.";
      }
      break;
    }
    case "close-dialog":
      lookupController?.abort();
      dialogs.close();
      break;
    case "add-bot":
      roomAction({ type: "add_bot", slot });
      break;
    case "remove-bot":
      roomAction({ type: "remove_bot", slot });
      break;
    case "toggle-slot":
      roomAction({
        type: "set_slot",
        slot,
        open: target.dataset.open === "true",
      });
      break;
    case "switch-team":
      roomAction({ type: "set_team", slot, team: Number(target.dataset.team) });
      break;
    case "mode-ffa":
    case "mode-teams":
      roomAction({
        type: "set_mode",
        mode: action === "mode-ffa" ? "ffa" : "teams",
      });
      break;
    case "change-role":
      roomAction({
        type: "set_role",
        observe: target.dataset.observe === "true",
      });
      break;
    case "start":
      roomAction({ type: "start" });
      break;
    case "back-room":
      roomAction({ type: "back_to_room" });
      break;
    case "buy":
      buy(target.dataset.spell as Spell);
      break;
    case "upgrade":
      upgrade(target.dataset.spell as Spell);
      break;
    case "select-slot":
      state.selectedSlot = slot;
      render();
      break;
    case "ready":
      if (state.pendingReorder) break;
      command({ type: "ready", ready: !localPlayer(state)?.ready });
      break;
    case "scoreboard":
      dialogs.scoreboard(state);
      break;
    case "follow-list":
      dialogs.follow(state);
      break;
    case "follow-prev":
      cycleFollow(-1);
      break;
    case "follow-next":
      cycleFollow(1);
      break;
    case "follow":
      selectFollow(Number(target.dataset.player));
      dialogs.close();
      break;
    case "reset-settings":
      settings = { ...DEFAULT_SETTINGS };
      saveSettings(settings);
      configureEngine();
      dialogs.settings(settings);
      break;
    case "reload":
      location.reload();
      break;
  }
}
document.addEventListener("click", (event) => {
  const target = (event.target as Element).closest<HTMLElement>(
    "[data-action]",
  );
  if (target && !target.hasAttribute("disabled"))
    handleAction(target).catch((error) =>
      toast(
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.",
      ),
    );
});
document.addEventListener("submit", (event) => {
  const form = event.target as HTMLFormElement;
  if (!form.dataset.form) return;
  event.preventDefault();
  if (form.dataset.form === "rename") {
    const name = String(new FormData(form).get("name") ?? "").trim();
    if (!name || [...name].length > 24 || /[\p{Cc}\p{Cf}<>]/u.test(name)) {
      dialogs.error("Choose 1–24 visible characters, without angle brackets.");
      return;
    }
    ensureConnection()
      .then(() => command({ type: "rename", name }))
      .catch((error) => dialogs.error(error.message));
  } else if (form.dataset.form === "join") {
    lookupInvite(String(new FormData(form).get("invite") ?? ""));
  } else if (form.dataset.form === "settings") {
    dialogs.close();
  }
});
document.addEventListener("change", (event) => {
  const target = event.target as HTMLSelectElement;
  if (target.matches(".bot-difficulty"))
    roomAction({
      type: "set_bot_difficulty",
      slot: Number(target.dataset.slot),
      difficulty: target.value,
    });
});
document.addEventListener("input", (event) => {
  const input = event.target as HTMLInputElement;
  if (!input.closest('[data-form="settings"]')) return;
  const name = input.name as keyof Settings;
  const value =
    input.type === "checkbox"
      ? input.checked
      : input.type === "range"
        ? Number(input.value)
        : input.value;
  settings = sanitizeSettings({ ...settings, [name]: value });
  saveSettings(settings);
  configureEngine();
  const output = document.querySelector(`[data-output="${name}"]`);
  if (output)
    output.textContent =
      String(value) +
      (name.endsWith("Volume") ? "%" : name === "cameraTilt" ? "°" : "");
});
document.addEventListener("keydown", (event) => {
  if (event.defaultPrevented) return;
  if (event.key === "Escape") {
    event.preventDefault();
    if (dialogs.kind) {
      lookupController?.abort();
      dialogs.close();
    } else if (state.screen === "loader") leave();
    else if (state.screen === "match") dialogs.menu(state);
    return;
  }
  if ((event.target as Element).matches("input,select,textarea")) return;
  if (
    event.key === "Tab" &&
    state.screen === "match" &&
    state.snapshot?.phase !== "shop" &&
    !dialogs.kind
  ) {
    event.preventDefault();
    dialogs.scoreboard(state);
  }
  if (
    (event.key === "[" || event.key === "]") &&
    state.screen === "match" &&
    !dialogs.kind &&
    !localPlayer(state)?.alive
  ) {
    event.preventDefault();
    cycleFollow(event.key === "[" ? -1 : 1);
  }
});
document.addEventListener("visibilitychange", syncInput);
window.addEventListener("blur", syncInput);
window.addEventListener("focus", syncInput);
guardCanvasMouse(document.querySelector<HTMLCanvasElement>("#game-canvas")!, () => {
  syncInput();
  return inputEnabled;
});
document
  .querySelector("#game-canvas")!
  .addEventListener("contextmenu", (event) => event.preventDefault());
dialogs.onChange = () => {
  state.menuOpen = !!dialogs.kind;
  spellReordering.cancel();
  syncInput();
};
window.addEventListener("pagehide", () => {
  analytics.flush();
  connection.close();
});
window.addEventListener("pageshow", (event) => {
  if (event.persisted) location.reload();
});
function deliverEngineInputs(raw: string | undefined): void {
  if (raw && state.connected && state.screen === "match") {
    try {
      const inputs = JSON.parse(raw);
      // The engine owns input gating and emits one neutral command on blur or
      // menu open. Deliver that stop even when the dialog has disabled input.
      if (Array.isArray(inputs)) connection.sendInputs(inputs as PlayerInput[]);
    } catch (error) {
      console.warn("Invalid engine input packet", error);
    }
  }
}
// Dispatch in the engine's input-producing frame; the transport retains all
// ordering, match-epoch, WebSocket backpressure and QUIC retry ownership.
engine.onInputs(deliverEngineInputs);
function frame(): void {
  deliverEngineInputs(engine.inputs());
  requestAnimationFrame(frame);
}
setInterval(() => {
  const diagnostics = engine.diagnostics();
  let controls = document.querySelector<HTMLElement>("[data-control-status]");
  if (
    state.connected &&
    state.screen === "match" &&
    state.engineReady &&
    diagnostics?.inputSynchronizing
  ) {
    if (!controls) {
      controls = document.createElement("div");
      controls.dataset.controlStatus = "synchronizing";
      controls.className = "connection-status";
      controls.setAttribute("role", "status");
      controls.textContent = "Synchronizing controls…";
      document.body.append(controls);
    }
  } else controls?.remove();
  let node = document.querySelector<HTMLElement>("#performance");
  if (settings.showPerformance && state.screen !== "loader") {
    if (!node) {
      node = document.createElement("div");
      node.id = "performance";
      document.body.append(node);
    }
    node.textContent = `${Math.round(diagnostics?.fps ?? 0)} fps · ${state.latency} ms · ${connection.transportKind === "webtransport" ? "QUIC" : "WS"}${diagnostics?.p95FrameMs ? ` · p95 ${diagnostics.p95FrameMs.toFixed(1)} ms` : ""}`;
  } else node?.remove();
}, 250);
// The public presence estimate is bucketed by minute, so refresh it even when
// no real player connects or disconnects.
setInterval(() => {
  if (state.screen === "home" && state.online != null) render();
}, 60_000);
configureEngine();
render();
requestAnimationFrame(frame);
ensureConnection().catch((error) => {
  state.online = null;
  render();
  toast(error.message);
});
ensureEngine().catch(() => {});
if (location.pathname.startsWith("/room/")) lookupInvite(location.href);
